import { useMemo, useState } from "react";
import { isUiMockMode } from "@/config/features";
import { BULK_EXPORT_PREVIEW_LIMIT } from "@/domain/invoices/bulkExportPreview";
import { needsClientStatusFilter } from "@/domain/invoices/invoiceFilterState";
import { filterInvoicesByStatuses } from "@/domain/invoices/filterInvoicesByStatus";
import { sortInvoices } from "@/domain/invoices/sortInvoices";
import { ApiErrorNotice } from "@/features/billing/components/ApiErrorNotice";
import { BillingGateModal } from "@/features/billing/components/BillingGateModal";
import { usePlanAccess } from "@/features/billing/hooks/usePlanAccess";
import type { GatedCapability } from "@/features/billing/hooks/usePlanAccess";
import type { BillingGateReason } from "@/types/api";
import { InvoiceTable } from "@/features/invoices/components/InvoiceTable";
import { downloadInvoicePdfDirect } from "@/lib/http/downloadDocument";
import type { CustomerRow, InvoiceRow } from "@/types/api";

type BulkExportPreviewPanelProps = {
  items: InvoiceRow[];
  customers: CustomerRow[];
  filtersStale: boolean;
  loading: boolean;
  error: unknown;
};

export function BulkExportPreviewPanel({
  items,
  customers,
  filtersStale,
  loading,
  error,
}: BulkExportPreviewPanelProps) {
  const { withGate } = usePlanAccess();
  const [billingGate, setBillingGate] = useState<{
    reason: BillingGateReason;
    capability: GatedCapability;
  } | null>(null);
  const [rowDownloadError, setRowDownloadError] = useState<unknown>(null);
  const [downloading, setDownloading] = useState<{
    invoiceId: string;
    kind: "pdf";
  } | null>(null);

  const showTable = !loading && !error;
  const empty = showTable && items.length === 0;

  const onRowDownload = (row: InvoiceRow) => {
    if (isUiMockMode()) return;
    const reason = withGate("inline_pdf", () => {
      setRowDownloadError(null);
      setDownloading({ invoiceId: row.id, kind: "pdf" });
      void downloadInvoicePdfDirect(row.id, "invoice_pdf", {
        pdfUrl: row.invoice_pdf_url,
        invoiceNumber: row.invoice_number,
      })
        .catch((e) => setRowDownloadError(e))
        .finally(() => setDownloading(null));
    });
    if (reason) {
      setBillingGate({ reason, capability: "inline_pdf" });
    }
  };

  return (
    <div className="panel invoices-workspace bulk-export-preview mt-4 overflow-visible">
      <div className="bulk-export-preview-head">
        <div>
          <h2 className="bulk-export-preview-title">Sample preview</h2>
          <p className="bulk-export-preview-lead">
            Up to {BULK_EXPORT_PREVIEW_LIMIT} sample rows only — Start export includes all
            matching invoices.
          </p>
        </div>
        {filtersStale ? (
          <p className="bulk-export-preview-stale" role="status">
            Filters changed — search again to refresh.
          </p>
        ) : null}
      </div>

      {rowDownloadError ? (
        <ApiErrorNotice
          error={rowDownloadError}
          context="download"
          className="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        />
      ) : null}

      {error ? (
        <ApiErrorNotice
          error={error}
          context="invoices"
          className="mx-4 mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        />
      ) : null}

      {loading ? (
        <p className="bulk-export-preview-status">Searching…</p>
      ) : null}

      {showTable && !empty ? (
        <InvoiceTable
          variant="pdfs"
          items={items}
          customers={customers}
          selected={new Set()}
          onToggle={() => {}}
          onRowDownload={onRowDownload}
          downloading={downloading}
          embedded
          fixedColumnLayout
          hostedLinkActions="open-only"
          showSelection={false}
        />
      ) : null}

      {empty ? (
        <p className="bulk-export-preview-status">No invoices match these filters.</p>
      ) : null}

      <BillingGateModal
        open={billingGate != null}
        reason={billingGate?.reason ?? null}
        capability={billingGate?.capability}
        onClose={() => setBillingGate(null)}
      />
    </div>
  );
}

export function useBulkExportPreviewItems(
  rawItems: InvoiceRow[] | undefined,
  appliedStatuses: string[],
) {
  return useMemo(() => {
    let rows = rawItems ?? [];
    if (isUiMockMode() && needsClientStatusFilter(appliedStatuses)) {
      rows = filterInvoicesByStatuses(rows, appliedStatuses);
    }
    return sortInvoices(rows, "created-desc");
  }, [rawItems, appliedStatuses]);
}
